import React, { useEffect, useState } from "react";
import PublicNav, { PublicFooter } from "@/components/PublicNav";

// Lightweight markdown-to-JSX renderer for our locked legal copy.
// Supports: h2 (###), h3 (####), unordered lists, bold (**), links [x](y),
// mailto: and https:// autolinks, blockquotes (>), paragraphs.
function renderInline(text) {
  const nodes = [];
  let rest = text;
  const push = (n) => nodes.push(n);
  let key = 0;
  while (rest.length) {
    // markdown link [text](url)
    let m = rest.match(/\[([^\]]+)\]\(([^)]+)\)/);
    // bold **x**
    let b = rest.match(/\*\*([^*]+)\*\*/);
    // autolink
    let al = rest.match(/(https?:\/\/[^\s)]+)|([\w.+-]+@[\w-]+\.[\w.-]+)/);
    const first = [m, b, al].filter(Boolean).sort((a, x) => a.index - x.index)[0];
    if (!first) { push(rest); break; }
    if (first.index > 0) push(rest.slice(0, first.index));
    if (first === m) {
      const [, txt, href] = m;
      push(<a key={key++} href={href} className="text-cyan-300 hover:text-cyan-200 underline">{txt}</a>);
      rest = rest.slice(first.index + first[0].length);
    } else if (first === b) {
      push(<strong key={key++} className="text-slate-100 font-semibold">{first[1]}</strong>);
      rest = rest.slice(first.index + first[0].length);
    } else {
      const link = first[0];
      const href = link.startsWith("http") ? link : `mailto:${link}`;
      push(<a key={key++} href={href} className="text-cyan-300 hover:text-cyan-200 underline">{link}</a>);
      rest = rest.slice(first.index + first[0].length);
    }
  }
  return nodes;
}

function Markdown({ src }) {
  const [md, setMd] = useState("");
  useEffect(() => {
    fetch(src).then((r) => r.text()).then(setMd).catch(() => setMd("Could not load."));
  }, [src]);

  if (!md) return <div className="text-sm text-slate-500">Loading…</div>;

  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let key = 0;
  let sectionIdx = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (/^---\s*$/.test(line)) { i++; continue; }
    if (/^#\s+/.test(line)) { i++; continue; } // ignore top-level headers
    if (/^##\s+/.test(line)) {
      out.push(<h1 key={key++} className="font-display text-3xl sm:text-4xl font-bold text-white mb-6">{line.replace(/^##\s+/, "")}</h1>);
      i++; continue;
    }
    const h2 = line.match(/^###\s+(.+)/);
    if (h2) {
      sectionIdx++;
      const idMatch = h2[1].match(/^(\d+)\./);
      const anchor = idMatch ? `section-${idMatch[1]}` : `section-${sectionIdx}`;
      out.push(<h2 key={key++} id={anchor} className="font-display text-xl font-semibold text-white mt-10 mb-3 scroll-mt-24">{h2[1]}</h2>);
      i++; continue;
    }
    const h3 = line.match(/^####\s+(.+)/);
    if (h3) {
      out.push(<h3 key={key++} className="font-display text-base font-semibold text-slate-100 mt-6 mb-2">{h3[1]}</h3>);
      i++; continue;
    }
    // list
    if (/^-\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^-\s+/, ""));
        i++;
      }
      out.push(<ul key={key++} className="list-disc pl-6 space-y-1 text-slate-300 mb-4">{items.map((t, j) => <li key={j}>{renderInline(t)}</li>)}</ul>);
      continue;
    }
    // blockquote
    if (/^>\s+/.test(line)) {
      const parts = [];
      while (i < lines.length && /^>\s+/.test(lines[i])) {
        parts.push(lines[i].replace(/^>\s+/, ""));
        i++;
      }
      out.push(<blockquote key={key++} className="border-l-2 border-cyan-500/50 pl-4 my-4 text-slate-300 italic">{renderInline(parts.join(" "))}</blockquote>);
      continue;
    }
    if (line.trim() === "") { i++; continue; }
    // paragraph: consume until blank line
    const para = [];
    while (i < lines.length && lines[i].trim() !== "" && !/^(#|-|>|---)/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    out.push(<p key={key++} className="text-slate-300 leading-relaxed mb-4">{renderInline(para.join(" "))}</p>);
  }
  return <>{out}</>;
}

export default function Legal({ kind }) {
  const isPrivacy = kind === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";
  const src = isPrivacy ? "/legal/privacy.md" : "/legal/terms.md";
  useEffect(() => {
    document.title = `${title} · Fexidoc`;
  }, [title]);
  return (
    <div className="min-h-screen">
      <PublicNav />
      <section className="max-w-3xl mx-auto px-5 py-16">
        <div className="mono text-xs uppercase tracking-[0.24em] text-cyan-300 mb-3">Legal</div>
        <div className="text-sm text-slate-400 mb-8">Fexidoc · ABN 48 580 643 756 · Effective date: 30 September 2026 · Last updated: 30 September 2026</div>
        <div className="fx-panel p-8" data-testid={`legal-${kind}`}>
          <Markdown src={src} />
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
